import { Box, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import {
  siDiscord,
  siFacebook,
  siInstagram,
  siKick,
  siTiktok,
  siX
} from 'simple-icons';
import { RPG_URL } from '../../../Landing/SiteHeader';

type RaffleFooterProps = {
  /** defaults to current year */
  year?: number;
  /** defaults to "Incrypt" */
  brandName?: string;
  /** add extra bottom space on mobile to clear a page's fixed bottom CTA
      (used by the giveaway page's "ENTER NOW" bar) */
  ctaInset?: boolean;
};

const LOGO =
  'https://incrypt-dynamic-website-images.s3.ap-southeast-2.amazonaws.com/logo.png';
const TERMS_URL =
  'https://incrypt-public-assets-global.s3.ap-southeast-2.amazonaws.com/documents/terms_and_conditions_05_08_2026.docx';
const ABN_LOOKUP_URL = 'https://abr.business.gov.au/ABN/View?id=20630834762';
const DISCORD_URL = 'https://discord.gg/W8FGSQgW3S';
const SUPPORT_EMAIL = 'support@incrypt.com.au';

const SOCIALS = [
  { label: 'X', href: 'https://x.com/incryptofficial', icon: siX },
  { label: 'Instagram', href: 'https://www.instagram.com/incryptau/', icon: siInstagram },
  { label: 'TikTok', href: 'https://www.tiktok.com/@incryptofficial', icon: siTiktok },
  { label: 'Discord', href: DISCORD_URL, icon: siDiscord },
  { label: 'Facebook', href: 'https://www.facebook.com/incryptcrypto/', icon: siFacebook },
  { label: 'Kick', href: 'https://kick.com/incryptsos', icon: siKick }
];

const linkSx = {
  color: '#a7b8c6',
  fontSize: 14.5,
  lineHeight: 1.5,
  textDecoration: 'none',
  transition: 'color 160ms ease',
  width: 'fit-content',
  '&:hover': { color: '#38e8ff' }
} as const;

/** Internal (router) or external (new-tab) footer link. */
const FLink = ({
  label,
  to,
  external
}: {
  label: string;
  to: string;
  external?: boolean;
}) =>
  external ? (
    <Box component="a" href={to} target="_blank" rel="noopener noreferrer" sx={linkSx}>
      {label}
    </Box>
  ) : (
    <Box component={Link} to={to} sx={linkSx}>
      {label}
    </Box>
  );

const FColumn = ({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.4 }}>
    <Typography
      sx={{
        font: '800 10px ui-monospace, Menlo, Consolas, monospace',
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        color: '#647889',
        mb: 0.6
      }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const RaffleFooter = ({
  year = new Date().getFullYear(),
  brandName = 'Incrypt',
  ctaInset = false
}: RaffleFooterProps) => {
  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        mt: 'auto',
        color: '#a7b8c6',
        background: 'linear-gradient(180deg, #0a1119 0%, #06090f 100%)',
        borderTop: '1px solid rgba(140,170,190,0.10)'
      }}
    >
      {/* subtle cyan hairline across the top edge */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background:
            'linear-gradient(90deg, transparent, rgba(56,232,255,0.5), transparent)'
        }}
      />

      <Container
        maxWidth="lg"
        sx={{
          px: { xs: 3, md: 5 },
          pt: { xs: 5, md: 7 },
          /* Tight, clean bottom by default. On pages with a fixed bottom CTA
             (the giveaway "ENTER NOW" bar), ctaInset adds mobile room so the
             bar never covers the legal line. */
          pb: ctaInset ? { xs: 13, lg: 5.5 } : { xs: 4, md: 5.5 }
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '1fr 1fr',
              md: '1.7fr 1fr 1fr 1fr'
            },
            gap: { xs: 4, md: 5 },
            alignItems: 'start'
          }}
        >
          {/* Brand */}
          <Box sx={{ maxWidth: 340 }}>
            <Box
              component={Link}
              to="/"
              aria-label="Incrypt home"
              sx={{ display: 'inline-flex' }}
            >
              <Box
                component="img"
                src={LOGO}
                alt="Incrypt"
                sx={{ height: 40, width: 'auto', display: 'block' }}
              />
            </Box>
            <Typography
              sx={{ mt: 2, fontSize: 14, lineHeight: 1.65, color: '#8ea3b3' }}
            >
              A digital collectible universe — collect cards, battle in the TCG,
              and go in the draw for $10,000 in INC every month.
            </Typography>
            <Box sx={{ mt: 2.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {SOCIALS.map((s) => (
                <Box
                  key={s.label}
                  component="a"
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  sx={{
                    width: 36,
                    height: 36,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: '10px',
                    color: '#a7b8c6',
                    border: '1px solid rgba(140,170,190,0.16)',
                    background: 'rgba(255,255,255,0.02)',
                    transition: 'transform 160ms ease, background 160ms ease, color 160ms ease, border-color 160ms ease',
                    '&:hover': {
                      color: '#04121a',
                      background: '#38e8ff',
                      borderColor: '#38e8ff',
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <Box
                    component="svg"
                    viewBox="0 0 24 24"
                    sx={{ width: 16, height: 16, fill: 'currentColor' }}
                  >
                    <path d={s.icon.path} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Explore */}
          <FColumn title="Explore">
            <FLink label="Giveaway" to="/giveaway" />
            <FLink label="Promo Pack" to="/promo" />
            <FLink label="RPG" to={RPG_URL} external />
            <FLink label="Digital Collectibles" to="/digital-collectibles" />
            <FLink label="Contact Us" to="/contact-us" />
            <FLink label="Log In" to="/login" />
          </FColumn>

          {/* Support */}
          <FColumn title="Support">
            <FLink label={SUPPORT_EMAIL} to={`mailto:${SUPPORT_EMAIL}`} external />
            <FLink label="Join our Discord" to={DISCORD_URL} external />
            <Typography sx={{ fontSize: 13, color: '#647889', mt: 0.4 }}>
              Replies within 24–48h
            </Typography>
          </FColumn>

          {/* Legal */}
          <FColumn title="Legal">
            <FLink label="Terms & Conditions" to={TERMS_URL} external />
            <FLink label="Privacy Policy" to="/privacy" />
          </FColumn>
        </Box>

        {/* Divider */}
        <Box
          sx={{
            mt: { xs: 4, md: 6 },
            mb: { xs: 2.5, md: 3 },
            height: '1px',
            background: 'rgba(140,170,190,0.10)'
          }}
        />

        {/* Bottom legal bar */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 2 },
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography sx={{ fontSize: 13, color: '#647889' }}>
            © {year} {brandName}. All rights reserved.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: 1.5, sm: 2.5 },
              alignItems: 'center'
            }}
          >
            <Typography sx={{ fontSize: 13, color: '#8ea3b3' }}>
              ABN{' '}
              <Box
                component="a"
                href={ABN_LOOKUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#8ea3b3',
                  textDecoration: 'none',
                  borderBottom: '1px dotted rgba(140,170,190,0.45)',
                  transition: 'color 160ms ease',
                  '&:hover': { color: '#38e8ff' }
                }}
              >
                20 630 834 762
              </Box>
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#8ea3b3' }}>
              Permit No. TP/03891
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default RaffleFooter;
